/**
 * Challenges lobby + attempt orchestration (hooks into legacy-app game loop).
 */
import {
  fetchChallengeStatus,
  postChallengeClaimReward,
  postChallengeFail,
  postChallengeSpinMode,
} from "./api";
import {
  advanceFlipCountdown,
  createFlipCountdown,
  drawFlipCountdown,
  syncFlipCountdown,
  type FlipCell,
} from "./flip-countdown";
import { drawChallengeLobbyTipText } from "./lobby-tips-draw";
import { hideChallengeRoulette, playChallengeRoulette, showChallengeRevealText } from "./roulette-ui";
import {
  constrainCursorOnDeformedEdge,
  createSquareFxState,
  drawSquareFxOutline,
  resetSquareFx,
  tickSquareFx,
  type SquareFxState,
} from "./square-fx";
import {
  challengeModeNeedsLevel,
  formatChallengeGoal,
  type ChallengeAttempt,
  type ChallengeDayStatus,
  type ChallengeLobbyTip,
} from "./types";

export type ChallengePhase =
  | "off"
  | "lobby"
  | "fade"
  | "mode_spin"
  | "mode_reveal"
  | "playing"
  | "death_seq"
  | "reward_spin"
  | "reward_reveal";

type ChallengeDeathStep = "hold" | "ball_fade" | "black_in" | "lost_hold" | "black_out";

export type ChallengeSfxKind =
  | "enter_cube"
  | "cube_break"
  | "fade_tick"
  | "countdown_tick"
  | "roulette_appear"
  | "roulette_tick"
  | "roulette_stop"
  | "reveal_show"
  | "reveal_hide"
  | "reward_appear"
  | "reward_claim"
  | "goal_complete"
  | "fail";

export type ChallengesHooks = {
  isSignedIn: () => boolean;
  openSignIn: () => void;
  initLobbyGame: () => void;
  startModeRun: (modeId: string, opts?: { levelIndex?: number | null }) => void | Promise<void>;
  endRunQuiet: () => void;
  returnToHub: () => void;
  /** Persist local/cloud personal best for the current run (even if the Challenge goal was not met). */
  persistRunBestIfAny: () => void;
  getMouse: () => { x: number; y: number };
  setMouse: (x: number, y: number) => void;
  getCanvasSize: () => { w: number; h: number };
  getCursorRadius: () => number;
  getScore: () => number;
  /** Mode-aware challenge progress (e.g. Progression uses level, not time-score). */
  getChallengeProgress: (modeId: string, metric: "score" | "time") => number;
  getPlayElapsedSec: () => number;
  setDotsBalance: (balance: number) => void;
  setReturnBtnLabel: (label: string) => void;
  hideHudForLobby: (hide: boolean) => void;
  setFadeOverlay: (alpha: number) => void;
  /** Pause/unpause the in-progress challenge run without destroying it (for claim). */
  setChallengeRunPaused: (paused: boolean) => void;
  /** Screen-edge pulse while the Challenge goal is met and the run continues. */
  setChallengeGoalCompleteFx: (active: boolean) => void;
  /** Freeze balls/hazards; cursor stays free. Optional ball draw alpha 0–1. */
  setChallengeDeathWorld: (opts: { frozen: boolean; ballAlpha: number }) => void;
  /** Clear balls, particles, trails, and in-run HUD chrome (no leftover visuals). */
  clearChallengeDeathWorld: () => void;
  /** Start ash disintegration (called when ball fade begins). */
  beginChallengeDeathAsh: () => void;
  /** Emit/update ash flakes during ball fade. `t` is 0–1 fade progress. */
  tickChallengeDeathAsh: (dt: number, t: number) => void;
  /** Draw ash flakes (after frozen scene). */
  drawChallengeDeathAsh: () => void;
  showChallengeLostScreen: (countdownLabel: string) => void;
  updateChallengeLostCountdown: (countdownLabel: string) => void;
  hideChallengeLostScreen: () => void;
  /** Smoothly duck game music while the red (locked) Challenges lobby is up. */
  setChallengeRedMusicDuck: (active: boolean) => void;
  playSfx: (kind: ChallengeSfxKind) => void;
};

function sfx(kind: ChallengeSfxKind) {
  hooks?.playSfx?.(kind);
}

let hooks: ChallengesHooks | null = null;

let phase: ChallengePhase = "off";
let dayStatus: ChallengeDayStatus = "available";
let available = true;
let activeTemplateCount = 0;
let attempt: ChallengeAttempt | null = null;
let fadeStart = 0;
let lastFadeTickAt = 0;
let playStartedAt = 0;
let playElapsedAccum = 0;
let lastPlayTick = 0;
/** Progress snapshot when the assigned mode run actually begins (blocks instant wins). */
let progressBaseline = 0;
/** Earliest time goal checks are allowed after the run starts. */
let goalCheckReadyAt = 0;
let returnArmed = false;
let returnArmUntil = 0;
let busy = false;
let squareBlocked = false;
/** Local midnight of the next challenge day (UTC ms), from server `dayEndsAt`. */
let dayEndsAtMs = 0;
let dayRolloverRefreshPending = false;
let deathStep: ChallengeDeathStep = "hold";
let deathStepStartedAt = 0;
/** Flip-clock cells for DD:HH:MM:SS. */
let countdownFlipCells: FlipCell[] = createFlipCountdown("00:00:00:00");
/** Last whole remaining seconds (for loud tick). */
let lastCountdownWholeSec = -1;
/** After a failed claim, briefly block re-triggering the goal check. */
let claimBackoffUntil = 0;
/** Visual-only edge highlight / squash / shatter for the lobby square. */
let squareFx: SquareFxState = createSquareFxState();
/** Active lobby tips from status GET. */
let lobbyTips: ChallengeLobbyTip[] = [];
/** Seconds spent in lobby this visit (paused while a tip is showing). */
let lobbyIdleSec = 0;
/** Tip ids already shown this lobby visit. */
const lobbyTipsShown = new Set<string>();
let activeLobbyTip: ChallengeLobbyTip | null = null;
let activeLobbyTipStartedAt = 0;
let activeLobbyTipAlpha = 0;
/** Extra ms added to countdown while tips freeze it. */
let countdownPausedAccumMs = 0;
let countdownPauseStartedAt = 0;
/** Goal reached this attempt — keep playing; claim/roulette waits until death (or Return). */
let goalCompleted = false;

const FADE_MS = 3000;
const RETURN_SURE_MS = 2000;
const SQUARE_FRAC = 0.22;
/** Hold frozen balls before they start fading. */
const DEATH_HOLD_MS = 2000;
/** Ball fade-out duration. */
const DEATH_BALL_FADE_MS = 3000;
/** Fade to black after balls are gone. */
const DEATH_BLACK_IN_MS = 3000;
/** Hold black with YOU LOST + countdown. */
const DEATH_LOST_HOLD_MS = 3500;
/** Fade from black into the red lobby square. */
const DEATH_BLACK_OUT_MS = 3000;

const COLOR_BLUE = "#b8ffff";
const COLOR_BLUE_GLOW = "#7efcff";
const COLOR_RED = "#c44a55";
const COLOR_RED_GLOW = "#a03842";
const COLOR_RED_TEXT = "rgba(255, 170, 178, 0.92)";

export function mountChallengesRuntime(h: ChallengesHooks) {
  hooks = h;
}

export function isChallengesActive(): boolean {
  return phase !== "off";
}

export function isChallengeLobby(): boolean {
  return phase === "lobby" || phase === "fade" || phase === "mode_spin" || phase === "mode_reveal";
}

export function isChallengePlaying(): boolean {
  return (
    phase === "playing" ||
    phase === "death_seq" ||
    phase === "reward_spin" ||
    phase === "reward_reveal"
  );
}

export function isChallengeAttemptPlaying(): boolean {
  return phase === "playing";
}

export function isChallengeGoalCompleted(): boolean {
  return goalCompleted && phase === "playing";
}

function clearChallengeGoalCompleteFx() {
  goalCompleted = false;
  hooks?.setChallengeGoalCompleteFx(false);
}

/** Mark the daily Challenge goal as met without claiming yet — keep playing. */
export function markChallengeGoalCompleted(): void {
  if (!hooks || phase !== "playing" || !attempt || goalCompleted) return;
  goalCompleted = true;
  sfx("goal_complete");
  hooks.setChallengeGoalCompleteFx(true);
}

export function isChallengeDeathSequence(): boolean {
  return phase === "death_seq";
}

/** True while claiming / showing the Dots reward roulette (ignore deaths). */
export function isChallengeRewardFlow(): boolean {
  return phase === "reward_spin" || phase === "reward_reveal";
}

/** True while frozen balls are still on screen (hold + ball fade). */
export function isChallengeDeathWorldActive(): boolean {
  return phase === "death_seq" && (deathStep === "hold" || deathStep === "ball_fade");
}

/** True while balls are crumbling into ash. */
export function isChallengeDeathBallFading(): boolean {
  return phase === "death_seq" && deathStep === "ball_fade";
}

/** True while the red square should be drawn under the outro fade. */
export function isChallengeDeathShowingLobby(): boolean {
  return phase === "death_seq" && deathStep === "black_out";
}

function lostCountdownLabel(): string {
  return formatCountdownDhms(getCountdownRemainingMs());
}

export function challengesPauseBlocked(): boolean {
  return isChallengesActive();
}

export function getChallengePhase(): ChallengePhase {
  return phase;
}

function squareRect(w: number, h: number) {
  const size = Math.min(w, h) * SQUARE_FRAC;
  const x = (w - size) / 2;
  const y = (h - size) / 2;
  return { x, y, size };
}

export function getChallengeSquare() {
  if (!hooks) return null;
  const { w, h } = hooks.getCanvasSize();
  return squareRect(w, h);
}

export function isChallengeSquareBlocked(): boolean {
  return squareBlocked;
}

function refreshBlocked() {
  squareBlocked = !available || dayStatus === "success" || dayStatus === "failed" || dayStatus === "in_progress";
}

function applyStatusFields(st: {
  available?: boolean;
  status?: ChallengeDayStatus;
  activeTemplateCount?: number;
  active?: ChallengeAttempt | null;
  dayEndsAt?: string | null;
  lobbyTips?: ChallengeLobbyTip[] | null;
}) {
  if (typeof st.available === "boolean") available = st.available;
  if (st.status) dayStatus = st.status;
  if (st.activeTemplateCount != null) {
    activeTemplateCount = Math.max(0, Math.floor(Number(st.activeTemplateCount) || 0));
  }
  if (st.active !== undefined) attempt = st.active;
  if (typeof st.dayEndsAt === "string" && st.dayEndsAt) {
    const t = Date.parse(st.dayEndsAt);
    dayEndsAtMs = Number.isFinite(t) ? t : 0;
  }
  if (Array.isArray(st.lobbyTips)) {
    lobbyTips = st.lobbyTips.filter((t) => t && String(t.text || "").trim());
  }
  refreshBlocked();
}

function formatCountdownDhms(remainingMs: number): string {
  const totalSec = Math.max(0, Math.floor(remainingMs / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(days)}:${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}

function getCountdownRemainingMs(): number {
  if (dayEndsAtMs <= 0) return 0;
  let rem = dayEndsAtMs - Date.now() + countdownPausedAccumMs;
  if (countdownPauseStartedAt > 0) rem += Date.now() - countdownPauseStartedAt;
  return Math.max(0, rem);
}

function beginCountdownPause() {
  if (countdownPauseStartedAt > 0) return;
  countdownPauseStartedAt = Date.now();
}

function endCountdownPause() {
  if (countdownPauseStartedAt <= 0) return;
  countdownPausedAccumMs += Date.now() - countdownPauseStartedAt;
  countdownPauseStartedAt = 0;
}

function tipMatchesSquare(tip: ChallengeLobbyTip): boolean {
  const state = String(tip.squareState || "both");
  if (state === "both") return true;
  if (state === "red") return squareBlocked;
  if (state === "blue") return !squareBlocked;
  return true;
}

function resetLobbyTipSession() {
  lobbyIdleSec = 0;
  lobbyTipsShown.clear();
  activeLobbyTip = null;
  activeLobbyTipStartedAt = 0;
  activeLobbyTipAlpha = 0;
  endCountdownPause();
}

function clearLobbyTipDisplay() {
  if (activeLobbyTip?.pauseCountdown) endCountdownPause();
  activeLobbyTip = null;
  activeLobbyTipStartedAt = 0;
  activeLobbyTipAlpha = 0;
}

function tickLobbyTips(now: number, dt: number) {
  if (phase !== "lobby") {
    clearLobbyTipDisplay();
    return;
  }

  if (activeLobbyTip) {
    const dur = Math.max(0.25, Number(activeLobbyTip.durationSeconds) || 4);
    const elapsed = (now - activeLobbyTipStartedAt) / 1000;
    const fadeIn = Math.min(1, elapsed / 0.25);
    const fadeOut = elapsed > dur - 0.3 ? Math.max(0, (dur - elapsed) / 0.3) : 1;
    activeLobbyTipAlpha = Math.min(fadeIn, fadeOut);
    if (elapsed >= dur) {
      lobbyTipsShown.add(activeLobbyTip.id);
      clearLobbyTipDisplay();
    }
    return;
  }

  lobbyIdleSec += Math.max(0, dt);
  const next = lobbyTips
    .filter((t) => tipMatchesSquare(t) && !lobbyTipsShown.has(t.id))
    .sort(
      (a, b) =>
        (Number(a.holdSeconds) || 0) - (Number(b.holdSeconds) || 0) ||
        (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0),
    )
    .find((t) => lobbyIdleSec >= Math.max(0, Number(t.holdSeconds) || 0));
  if (!next) return;
  activeLobbyTip = next;
  activeLobbyTipStartedAt = now;
  activeLobbyTipAlpha = 0;
  if (next.pauseCountdown && squareBlocked) beginCountdownPause();
}

function shouldShowRedCountdown(): boolean {
  if (!squareBlocked || dayEndsAtMs <= 0) return false;
  if (phase === "lobby") return true;
  if (phase === "death_seq" && (deathStep === "lost_hold" || deathStep === "black_out")) return true;
  return false;
}

function shouldDuckRedMusic(): boolean {
  if (!isChallengesActive() || !squareBlocked) return false;
  // Duck on locked lobby and loss cinematic — not during a live attempt.
  return phase === "lobby" || phase === "death_seq";
}

function syncChallengeRedMusicDuck() {
  hooks?.setChallengeRedMusicDuck(shouldDuckRedMusic());
}

function resetCountdownFlip(label = "00:00:00:00") {
  countdownFlipCells = createFlipCountdown(label);
  lastCountdownWholeSec = -1;
}

/** Advance flip digits, play loud tick on each second drop. */
function tickChallengeRedCountdown(dt: number) {
  if (!shouldShowRedCountdown()) {
    advanceFlipCountdown(countdownFlipCells, dt);
    return;
  }
  const remaining = getCountdownRemainingMs();
  const wholeSec = Math.floor(remaining / 1000);
  const label = formatCountdownDhms(remaining);
  if (lastCountdownWholeSec >= 0 && wholeSec < lastCountdownWholeSec) {
    sfx("countdown_tick");
  }
  if (lastCountdownWholeSec !== wholeSec) {
    lastCountdownWholeSec = wholeSec;
    syncFlipCountdown(countdownFlipCells, label);
  }
  advanceFlipCountdown(countdownFlipCells, dt);
}

export function getChallengeCountdownFlipCells(): FlipCell[] {
  return countdownFlipCells;
}

async function refreshStatus() {
  const st = await fetchChallengeStatus();
  if (!st) {
    available = false;
    dayStatus = "available";
    activeTemplateCount = 0;
    attempt = null;
    dayEndsAtMs = 0;
    lobbyTips = [];
    refreshBlocked();
    return null;
  }
  applyStatusFields(st);
  // in_progress with active attempt but we just entered lobby → treat square as blocked until resolved
  if (st.status === "in_progress" && st.active) {
    // Abandoned mid-attempt: auto-fail so the square goes red
    try {
      const failed = await postChallengeFail(st.active.token);
      applyStatusFields(failed);
    } catch {
      dayStatus = "failed";
      available = false;
      refreshBlocked();
    }
  }
  dayRolloverRefreshPending = false;
  return st;
}

export async function openChallengesFlow(): Promise<void> {
  if (!hooks) return;
  if (!hooks.isSignedIn()) {
    hooks.openSignIn();
    return;
  }
  hideChallengeRoulette();
  hooks.setFadeOverlay(0);
  hooks.setReturnBtnLabel("Return");
  returnArmed = false;
  busy = false;
  phase = "lobby";
  try {
    await refreshStatus();
  } catch {
    available = false;
    dayStatus = "available";
    refreshBlocked();
  }
  if (typeof document !== "undefined") document.body.classList.add("challenge-active");
  hooks.hideHudForLobby(true);
  hooks.initLobbyGame();
  const { w, h } = hooks.getCanvasSize();
  hooks.setMouse(w / 2, h * 0.78);
  if (squareBlocked && dayEndsAtMs > 0) {
    resetCountdownFlip(formatCountdownDhms(getCountdownRemainingMs()));
  } else {
    resetCountdownFlip();
  }
  resetSquareFx(squareFx);
  resetLobbyTipSession();
  countdownPausedAccumMs = 0;
  clearChallengeGoalCompleteFx();
  syncChallengeRedMusicDuck();
}

export function exitChallengesToHub() {
  hideChallengeRoulette();
  clearChallengeGoalCompleteFx();
  if (hooks) {
    hooks.setChallengeDeathWorld({ frozen: false, ballAlpha: 1 });
    hooks.hideChallengeLostScreen();
    hooks.setFadeOverlay(0);
    hooks.hideHudForLobby(false);
    hooks.setReturnBtnLabel("Return");
    hooks.endRunQuiet();
    hooks.returnToHub();
  }
  phase = "off";
  attempt = null;
  returnArmed = false;
  busy = false;
  deathStep = "hold";
  deathStepStartedAt = 0;
  resetCountdownFlip();
  resetSquareFx(squareFx);
  resetLobbyTipSession();
  countdownPausedAccumMs = 0;
  if (hooks) {
    hooks.setChallengeRedMusicDuck(false);
  }
  if (typeof document !== "undefined") {
    document.body.classList.remove("challenge-lobby", "challenge-active");
  }
}

/** Hard reset when leaving via goToMenu / logout without Return. */
export function resetChallengesState() {
  hideChallengeRoulette();
  clearChallengeGoalCompleteFx();
  phase = "off";
  attempt = null;
  returnArmed = false;
  busy = false;
  deathStep = "hold";
  deathStepStartedAt = 0;
  // Keep last known day status until next openChallengesFlow refresh
  // (do not force available=true — that briefly unlocked a used day).
  if (hooks) {
    hooks.setChallengeDeathWorld({ frozen: false, ballAlpha: 1 });
    hooks.hideChallengeLostScreen();
    hooks.setFadeOverlay(0);
    hooks.setChallengeRedMusicDuck(false);
  }
  resetCountdownFlip();
  resetSquareFx(squareFx);
  resetLobbyTipSession();
  countdownPausedAccumMs = 0;
  if (typeof document !== "undefined") {
    document.body.classList.remove("challenge-lobby", "challenge-active");
  }
}

async function failAttemptAndLobby() {
  if (!hooks) return;
  const token = attempt?.token;
  busy = true;
  hooks.persistRunBestIfAny();
  clearChallengeGoalCompleteFx();
  sfx("fail");
  try {
    const st = await postChallengeFail(token);
    applyStatusFields(st);
  } catch {
    available = false;
    dayStatus = "failed";
    attempt = null;
    refreshBlocked();
  }
  hideChallengeRoulette();
  hooks.setChallengeDeathWorld({ frozen: false, ballAlpha: 1 });
  hooks.hideChallengeLostScreen();
  hooks.setFadeOverlay(0);
  hooks.setReturnBtnLabel("Return");
  returnArmed = false;
  hooks.hideHudForLobby(true);
  hooks.endRunQuiet();
  hooks.initLobbyGame();
  const { w, h } = hooks.getCanvasSize();
  hooks.setMouse(w / 2, h * 0.78);
  phase = "lobby";
  busy = false;
  if (squareBlocked && dayEndsAtMs > 0) {
    resetCountdownFlip(formatCountdownDhms(Math.max(0, dayEndsAtMs - Date.now())));
  }
  resetSquareFx(squareFx);
  syncChallengeRedMusicDuck();
}

/**
 * Loss cinematic: freeze balls → hold 2s → ash fade 3s → black fade-in 3s →
 * YOU LOST + countdown hold → black fade-out 3s to red square.
 * If the Challenge goal was already met, claim the reward roulette instead.
 */
export async function onChallengeDeathFail(): Promise<void> {
  if (!hooks || phase !== "playing" || busy) return;
  if (goalCompleted && attempt) {
    await claimChallengeRewardAndPresent();
    return;
  }
  busy = true;
  phase = "death_seq";
  deathStep = "hold";
  deathStepStartedAt = performance.now();
  returnArmed = false;
  hooks.setReturnBtnLabel("Return");
  clearChallengeGoalCompleteFx();
  sfx("fail");
  hooks.hideHudForLobby(true);
  hooks.hideChallengeLostScreen();
  hooks.setFadeOverlay(0);
  hooks.setChallengeDeathWorld({ frozen: true, ballAlpha: 1 });
  syncChallengeRedMusicDuck();

  const token = attempt?.token;
  attempt = null;
  available = false;
  dayStatus = "failed";
  refreshBlocked();
  try {
    const st = await postChallengeFail(token);
    applyStatusFields(st);
  } catch {
    refreshBlocked();
  }
}

/** Drive the death cinematic from the game loop. Returns true while still running. */
export function tickChallengeDeathSequence(now: number, dt = 1 / 60): boolean {
  if (!hooks || phase !== "death_seq") return false;
  tickChallengeRedCountdown(dt);
  syncChallengeRedMusicDuck();
  const elapsed = now - deathStepStartedAt;

  if (deathStep === "hold") {
    hooks.setChallengeDeathWorld({ frozen: true, ballAlpha: 1 });
    if (elapsed >= DEATH_HOLD_MS) {
      deathStep = "ball_fade";
      deathStepStartedAt = now;
      hooks.beginChallengeDeathAsh();
    }
    return true;
  }

  if (deathStep === "ball_fade") {
    const t = Math.min(1, elapsed / DEATH_BALL_FADE_MS);
    hooks.setChallengeDeathWorld({ frozen: true, ballAlpha: 1 - t });
    hooks.tickChallengeDeathAsh(dt, t);
    if (t >= 1) {
      hooks.clearChallengeDeathWorld();
      hooks.setChallengeDeathWorld({ frozen: true, ballAlpha: 0 });
      deathStep = "black_in";
      deathStepStartedAt = now;
    }
    return true;
  }

  if (deathStep === "black_in") {
    const t = Math.min(1, elapsed / DEATH_BLACK_IN_MS);
    hooks.setFadeOverlay(t);
    if (t >= 1) {
      hooks.setFadeOverlay(1);
      hooks.setChallengeDeathWorld({ frozen: false, ballAlpha: 1 });
      hooks.hideHudForLobby(true);
      hooks.endRunQuiet();
      hooks.initLobbyGame();
      const { w, h } = hooks.getCanvasSize();
      hooks.setMouse(w / 2, h * 0.78);
      if (squareBlocked && dayEndsAtMs > 0) {
        resetCountdownFlip(formatCountdownDhms(Math.max(0, dayEndsAtMs - Date.now())));
      }
      hooks.showChallengeLostScreen(lostCountdownLabel());
      deathStep = "lost_hold";
      deathStepStartedAt = now;
      resetSquareFx(squareFx);
      syncChallengeRedMusicDuck();
    }
    return true;
  }

  if (deathStep === "lost_hold") {
    hooks.setFadeOverlay(1);
    hooks.updateChallengeLostCountdown(lostCountdownLabel());
    if (elapsed >= DEATH_LOST_HOLD_MS) {
      deathStep = "black_out";
      deathStepStartedAt = now;
    }
    return true;
  }

  if (deathStep === "black_out") {
    const t = Math.min(1, elapsed / DEATH_BLACK_OUT_MS);
    hooks.updateChallengeLostCountdown(lostCountdownLabel());
    hooks.setFadeOverlay(1 - t);
    // Drive red-square edge FX under the veil as the lobby reappears.
    const m = hooks.getMouse();
    const sq = getChallengeSquare();
    if (sq) {
      tickSquareFx(squareFx, {
        sq,
        mx: m.x,
        my: m.y,
        cursorR: hooks.getCursorRadius(),
        dt,
        blocked: true,
        allowBreak: false,
      });
    }
    if (t >= 1) {
      hooks.setFadeOverlay(0);
      hooks.hideChallengeLostScreen();
      phase = "lobby";
      deathStep = "hold";
      deathStepStartedAt = 0;
      busy = false;
      syncChallengeRedMusicDuck();
      return false;
    }
    return true;
  }

  return false;
}

/** Return button handling. Returns true if consumed. */
export function handleChallengeReturn(): boolean {
  if (!isChallengesActive() || !hooks) return false;
  if (phase === "death_seq") {
    return true; // ignore during loss cinematic
  }
  if (phase === "lobby" || phase === "fade" || phase === "mode_spin" || phase === "mode_reveal") {
    // During fade/spin after server lock — fail the day
    if ((phase === "mode_spin" || phase === "mode_reveal") && attempt) {
      void failAttemptAndLobby().then(() => exitChallengesToHub());
      return true;
    }
    exitChallengesToHub();
    return true;
  }
  if (phase === "reward_spin" || phase === "reward_reveal") {
    return true; // ignore during reward
  }
  if (phase === "playing") {
    const now = performance.now();
    if (!returnArmed || now > returnArmUntil) {
      returnArmed = true;
      returnArmUntil = now + RETURN_SURE_MS;
      hooks.setReturnBtnLabel("Sure?");
      return true;
    }
    returnArmed = false;
    hooks.setReturnBtnLabel("Return");
    if (goalCompleted && attempt) {
      void claimChallengeRewardAndPresent();
    } else {
      void failAttemptAndLobby();
    }
    return true;
  }
  return false;
}

export function tickChallengeReturnSure(now: number) {
  if (!returnArmed || !hooks) return;
  if (now > returnArmUntil) {
    returnArmed = false;
    hooks.setReturnBtnLabel("Return");
  }
}

function clampToSquare(px: number, py: number, sq: { x: number; y: number; size: number }, r: number) {
  const minX = sq.x + r;
  const maxX = sq.x + sq.size - r;
  const minY = sq.y + r;
  const maxY = sq.y + sq.size - r;
  return {
    x: Math.min(maxX, Math.max(minX, px)),
    y: Math.min(maxY, Math.max(minY, py)),
  };
}

/** Cursor rides the deformed pocket wall (red: never through; blue: until shatter). */
export function constrainChallengeLobbyCursor() {
  if (!hooks || (phase !== "lobby" && !isChallengeDeathShowingLobby())) return;
  if (!squareBlocked && phase !== "lobby") return;
  const m = hooks.getMouse();
  const r = hooks.getCursorRadius();
  const sq = getChallengeSquare();
  if (!sq) return;

  const held = constrainCursorOnDeformedEdge(m.x, m.y, sq, squareFx, r, {
    blocked: squareBlocked,
    shattered: squareFx.shattered,
  });
  if (held) hooks.setMouse(held.x, held.y);
}

function beginChallengeEnterFade(now: number) {
  if (!hooks) return;
  clearLobbyTipDisplay();
  phase = "fade";
  fadeStart = now;
  lastFadeTickAt = now;
  sfx("cube_break");
  sfx("enter_cube");
  hooks.setFadeOverlay(0);
  syncChallengeRedMusicDuck();
}

export function tickChallengeLobby(now: number, dt = 1 / 60) {
  if (!hooks || phase === "off") return;
  tickChallengeReturnSure(now);
  tickChallengeRedCountdown(dt);
  tickLobbyTips(now, dt);
  syncChallengeRedMusicDuck();

  if (phase === "lobby") {
    const m = hooks.getMouse();
    const sq = getChallengeSquare();
    if (!sq) return;
    const canEnter =
      !squareBlocked && available !== false && activeTemplateCount > 0 && !busy;
    const broke = tickSquareFx(squareFx, {
      sq,
      mx: m.x,
      my: m.y,
      cursorR: hooks.getCursorRadius(),
      dt,
      blocked: squareBlocked,
      allowBreak: canEnter,
    });
    if (broke && canEnter) {
      beginChallengeEnterFade(now);
    }
    return;
  }

  if (phase === "fade") {
    const t = Math.min(1, (now - fadeStart) / FADE_MS);
    hooks.setFadeOverlay(t);
    if (now - lastFadeTickAt >= 420) {
      lastFadeTickAt = now;
      sfx("fade_tick");
    }
    const sq = getChallengeSquare();
    if (sq) {
      const m = hooks.getMouse();
      const r = Math.min(hooks.getCursorRadius(), sq.size * 0.2);
      const c = clampToSquare(m.x, m.y, sq, r);
      hooks.setMouse(c.x, c.y);
      tickSquareFx(squareFx, {
        sq,
        mx: c.x,
        my: c.y,
        cursorR: r,
        dt,
        blocked: false,
        allowBreak: false,
      });
    }
    if (t >= 1 && !busy) {
      void beginModeSpin();
    }
  }
}

async function beginModeSpin() {
  if (!hooks || busy) return;
  busy = true;
  phase = "mode_spin";
  hooks.setFadeOverlay(1);
  try {
    const result = await postChallengeSpinMode();
    attempt = result.attempt;
    applyStatusFields(result);
    const segments = (result.segments || []).map((s) => ({ label: s.modeLabel }));
    sfx("roulette_appear");
    await playChallengeRoulette({
      segments,
      resultIndex: result.resultIndex,
      durationMs: 5000,
      onTick: () => sfx("roulette_tick"),
      onStop: () => sfx("roulette_stop"),
    });
    phase = "mode_reveal";
    sfx("reveal_show");
    await showChallengeRevealText(
      [
        result.attempt.modeLabel,
        formatChallengeGoal(
          result.attempt.metric,
          result.attempt.goalValue,
          result.attempt.modeId,
          result.attempt.levelIndex,
        ),
      ],
      1800,
    );
    sfx("reveal_hide");
    phase = "playing";
    playStartedAt = performance.now();
    playElapsedAccum = 0;
    lastPlayTick = playStartedAt;
    hooks.hideHudForLobby(false);
    hooks.setFadeOverlay(0);
    hooks.setReturnBtnLabel("Return");
    await Promise.resolve(
      hooks.startModeRun(result.attempt.modeId, { levelIndex: result.attempt.levelIndex }),
    );
    // Capture after the mode bootstraps (level 1, score 0, etc.).
    progressBaseline = hooks.getChallengeProgress(result.attempt.modeId, result.attempt.metric);
    goalCheckReadyAt = performance.now() + 900;
    clearChallengeGoalCompleteFx();
    syncChallengeRedMusicDuck();
  } catch (e) {
    console.warn("[challenges] spin-mode failed", e);
    phase = "lobby";
    hooks.setFadeOverlay(0);
    resetSquareFx(squareFx);
    try {
      await refreshStatus();
    } catch {
      /* ignore */
    }
    syncChallengeRedMusicDuck();
  } finally {
    busy = false;
  }
}

export function noteChallengePlayFrame(now: number, paused: boolean) {
  if (phase !== "playing") return;
  if (paused) {
    lastPlayTick = now;
    return;
  }
  if (lastPlayTick > 0) playElapsedAccum += (now - lastPlayTick) / 1000;
  lastPlayTick = now;
}

export function getChallengePlayElapsedSec(): number {
  return playElapsedAccum;
}

/** SpeedRun / Golf challenge: finish the assigned level before the time limit. */
export function isChallengeTimedLevelAttempt(): boolean {
  return phase === "playing" && !!attempt && challengeModeNeedsLevel(attempt.modeId);
}

/** Remaining seconds on the SpeedRun/Golf challenge countdown (0 when not applicable). */
export function getChallengeCountdownRemainingSec(): number {
  if (!attempt || !challengeModeNeedsLevel(attempt.modeId)) return 0;
  return Math.max(0, Math.max(1, attempt.goalValue) - playElapsedAccum);
}

/** True when the SpeedRun/Golf challenge time limit ran out before the goal was met. */
export function checkChallengeTimeExpired(): boolean {
  if (!isChallengeTimedLevelAttempt() || !attempt || goalCompleted) return false;
  if (performance.now() < goalCheckReadyAt) return false;
  return playElapsedAccum >= attempt.goalValue;
}

export function checkChallengeGoalMet(): boolean {
  if (phase !== "playing" || !attempt || !hooks) return false;
  if (performance.now() < goalCheckReadyAt) return false;
  if (performance.now() < claimBackoffUntil) return false;
  // SpeedRun / Golf: only finishing the level counts (beginSpeedrunWin / beginPenaltyWin).
  if (challengeModeNeedsLevel(attempt.modeId)) return false;
  if (attempt.metric === "time") {
    return playElapsedAccum >= attempt.goalValue;
  }
  const progress = hooks.getChallengeProgress(attempt.modeId, attempt.metric);
  // Must reach the configured goal AND improve past the value at run start
  // (e.g. Progression starts at level 1 — a goal of 1 must not auto-complete).
  return progress >= attempt.goalValue && progress > progressBaseline;
}

function goToChallengeLobbyAfterAttempt() {
  if (!hooks) return;
  clearChallengeGoalCompleteFx();
  hooks.setFadeOverlay(0);
  hooks.hideHudForLobby(true);
  hooks.setChallengeRunPaused(false);
  hooks.endRunQuiet();
  hooks.initLobbyGame();
  const { w, h } = hooks.getCanvasSize();
  hooks.setMouse(w / 2, h * 0.78);
  phase = "lobby";
  busy = false;
  countdownPausedAccumMs = 0;
  resetLobbyTipSession();
  if (squareBlocked && dayEndsAtMs > 0) {
    resetCountdownFlip(formatCountdownDhms(getCountdownRemainingMs()));
  }
  resetSquareFx(squareFx);
  syncChallengeRedMusicDuck();
}

async function presentChallengeReward(claim: {
  rewardAmount?: number | null;
  rewardIndex?: number;
  rewardSegments?: number[];
  dotsBalance?: number;
  alreadyClaimed?: boolean;
}) {
  if (!hooks) return;
  if (typeof claim.dotsBalance === "number") hooks.setDotsBalance(claim.dotsBalance);
  const amount = Math.floor(Number(claim.rewardAmount) || 0);
  const segments = (claim.rewardSegments || []).map((n) => ({ label: String(n) }));
  if (amount > 0 && segments.length > 0 && !claim.alreadyClaimed) {
    sfx("reward_appear");
    await playChallengeRoulette({
      segments,
      resultIndex: Math.max(0, Math.floor(Number(claim.rewardIndex) || 0)),
      durationMs: 5000,
      onTick: () => sfx("roulette_tick"),
      onStop: () => sfx("roulette_stop"),
    });
    phase = "reward_reveal";
    sfx("reveal_show");
    await showChallengeRevealText([`+${amount} Dots`], 1800);
    sfx("reward_claim");
    sfx("reveal_hide");
  } else if (amount > 0) {
    phase = "reward_reveal";
    sfx("reveal_show");
    await showChallengeRevealText([`+${amount} Dots`], 1800);
    sfx("reward_claim");
    sfx("reveal_hide");
  } else if (claim.alreadyClaimed) {
    phase = "reward_reveal";
    sfx("reveal_show");
    await showChallengeRevealText(["Reward already claimed"], 1400);
    sfx("reveal_hide");
  }
}

/** Goal met mid-run: keep playing with edge FX. Reward roulette waits until death/Return. */
export function onChallengeGoalReached(): void {
  markChallengeGoalCompleted();
}

/** Claim Dots reward + roulette after the run ends with the goal already met. */
async function claimChallengeRewardAndPresent(): Promise<void> {
  if (!hooks || !attempt || busy) return;
  busy = true;
  phase = "reward_spin";
  hooks.setReturnBtnLabel("Return");
  returnArmed = false;
  hooks.persistRunBestIfAny();
  clearChallengeGoalCompleteFx();
  const token = attempt.token;
  const modeId = attempt.modeId;
  const metric = attempt.metric;
  const goalValue = attempt.goalValue;
  let score = hooks.getChallengeProgress(modeId, metric);
  let timeSec = playElapsedAccum;
  if (challengeModeNeedsLevel(modeId)) {
    // Finished the level under the time limit (validated server-side).
    score = 1;
  } else if (metric === "score") {
    score = Math.max(score, goalValue);
  } else {
    timeSec = Math.max(timeSec, goalValue);
  }

  hooks.setChallengeRunPaused(true);
  hooks.setFadeOverlay(1);

  let claim: Awaited<ReturnType<typeof postChallengeClaimReward>> | null = null;
  try {
    claim = await postChallengeClaimReward(token, { score, timeSec });
  } catch (e) {
    const err = e as { status?: number; data?: Record<string, unknown> };
    const data = (err?.data || {}) as Record<string, unknown>;
    console.warn("[challenges] claim failed", e);

    if (data.status === "success" || data.alreadyClaimed === true) {
      applyStatusFields(data as Parameters<typeof applyStatusFields>[0]);
      attempt = null;
      hooks.endRunQuiet();
      try {
        await presentChallengeReward({
          rewardAmount: typeof data.rewardAmount === "number" ? data.rewardAmount : null,
          rewardIndex: typeof data.rewardIndex === "number" ? data.rewardIndex : 0,
          rewardSegments: Array.isArray(data.rewardSegments)
            ? (data.rewardSegments as number[])
            : [],
          dotsBalance: typeof data.dotsBalance === "number" ? data.dotsBalance : undefined,
          alreadyClaimed: true,
        });
      } catch (presentErr) {
        console.warn("[challenges] reward presentation failed", presentErr);
      }
      goToChallengeLobbyAfterAttempt();
      return;
    }

    // Claim failed after death/exit: still end the day as success-ish lobby via refresh.
    claimBackoffUntil = performance.now() + 2500;
    phase = "playing";
    busy = false;
    goalCompleted = true;
    hooks.setChallengeRunPaused(false);
    hooks.setFadeOverlay(0);
    hooks.setChallengeGoalCompleteFx(true);
    return;
  }

  applyStatusFields(claim);
  attempt = null;
  hooks.endRunQuiet();
  try {
    await presentChallengeReward(claim);
  } catch (presentErr) {
    console.warn("[challenges] reward presentation failed", presentErr);
  }
  goToChallengeLobbyAfterAttempt();
}

export function drawChallengeLobbySquare(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
) {
  if (phase !== "lobby" && phase !== "fade" && !isChallengeDeathShowingLobby()) return;
  const sq = squareRect(canvasW, canvasH);
  const blocked = squareBlocked;
  const color = blocked ? COLOR_RED : COLOR_BLUE;
  const glow = blocked ? COLOR_RED_GLOW : COLOR_BLUE_GLOW;
  const highlightColor = blocked ? "#ff8a96" : "#ffffff";
  const highlightGlow = blocked ? "#ff5a6a" : "#dfffff";

  drawSquareFxOutline(ctx, sq, squareFx, {
    blocked,
    color,
    glow,
    highlightColor,
    highlightGlow,
  });

  if (blocked && dayEndsAtMs > 0) {
    const remaining = getCountdownRemainingMs();
    if (remaining <= 0 && countdownPauseStartedAt <= 0) {
      if (!dayRolloverRefreshPending) {
        dayRolloverRefreshPending = true;
        void refreshStatus().catch(() => {
          dayRolloverRefreshPending = false;
        });
      }
    }
    const label = formatCountdownDhms(remaining);
    // Keep flip cells synced if draw runs before lobby tick this frame.
    if (lastCountdownWholeSec < 0) {
      lastCountdownWholeSec = Math.floor(remaining / 1000);
      syncFlipCountdown(countdownFlipCells, label);
    }
    const fontSize = Math.max(10, Math.floor(sq.size * 0.11));
    ctx.save();
    ctx.shadowBlur = 0;
    drawFlipCountdown(
      ctx,
      countdownFlipCells,
      sq.x + sq.size / 2,
      sq.y + sq.size / 2,
      fontSize,
      COLOR_RED_TEXT,
      sq.size * 0.88,
      activeLobbyTip?.hideCountdown ? 1 - activeLobbyTipAlpha : 1,
    );
    ctx.restore();
  }

  if (activeLobbyTip && activeLobbyTipAlpha > 0.01) {
    drawChallengeLobbyTipText(ctx, activeLobbyTip, {
      square: sq,
      alpha: activeLobbyTipAlpha,
      color: blocked ? COLOR_RED_TEXT : "#ffffff",
      shadowColor: blocked ? COLOR_RED_GLOW : "rgba(180, 230, 255, 0.85)",
    });
  }
}
